// Styles
import '../../MapStyles/MapMenu.scss';
import 'leaflet.markercluster';

// Services
import { Api } from '@services';

// Utils
import { capitalize, getFormattedDateTime, useExportDownload } from '@utils';
import { debounce } from 'lodash';

// Libraries
import * as L from 'leaflet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';

// Components
import { Icon, Loading, Text } from '@components';
import { FilterContainer } from '@containers';
import { V2FilterMapList } from '../../../../../../components/AssetFilter/functions/V2FilterMapList';

// Store
import { useMapStore } from '../../MapStore/UseMapStore';

export function MapInfoList({
  type,
  listData,
  listMarkers,
  listRefs,
  fetchData = async () => {},
  visible,
  disabledFilters,
  reset,
}: {
  type: string;
  listData: any;
  listMarkers: any;
  listRefs: any;
  fetchData?: (payload: any, type: any) => Promise<any>;
  visible: boolean;
  disabledFilters: string[];
  reset: () => void;
}) {
  const setSelected = useMapStore((state) => state.setSelected);
  const setShowMore = useMapStore((state) => state.setShowMore);
  const mapName = useMapStore((state) => state.mapName);
  const mapID = useMapStore((state) => state.mapID);

  const [title, setTitle] = useState<string>(null);
  const [sort, setSort] = useState<any>({ field: 'Name', ascending: true });
  const [search, setSearch] = useState<string>('');
  const [andOr, setAndOr] = useState<string>('and');
  const [assetFilters, setAssetFilters] = useState<any>([]);
  const [csvData, setCsvData] = useState(null);
  const [csvName, setCsvName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showExportButton, setShowExportButton] = useState<boolean>(false);
  const history = useHistory();
  const isFirstRender = useRef(true);

  useExportDownload(csvData, csvName);

  // Assets only
  const debounceFetchData = useCallback(
    debounce((assetFilters, andOr, sort, search, selected) => {
      setIsLoading(true);
      fetchData(V2FilterMapList(assetFilters, andOr, sort, search), selected).then(() => {
        setIsLoading(false);
      });
    }, 500),
    [],
  );

  useEffect(() => {
    if (type) {
      type === 'stationary' ? setTitle('Stationary Assets') : setTitle(capitalize(type) + 's');
    } else {
      setTitle(null);
    }
  }, [type]);

  // Only asset refetches from the server
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (type !== 'asset') setShowExportButton(true);
      return;
    }

    if (type === 'asset') {
      setIsLoading(true);
      if (search !== '' || assetFilters.length > 0) {
        debounceFetchData(assetFilters, andOr, sort, search, type);
        setShowExportButton(true);
      } else {
        reset();
        setShowExportButton(false);
        setIsLoading(false);
      }
    }
  }, [type, assetFilters, andOr, sort, search]);

  // Local search/sort for place/gateway/stationary
  const displayedFeatures = useMemo(() => {
    if (type === 'asset') return listData?.features ?? [];
    if (!listData?.features) return [];

    let features = listData.features;

    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      features = features.filter((f) => f.properties?.Name?.toLowerCase().includes(q));
    }

    features = [...features].sort((a, b) => {
      const aVal = a.properties?.[sort.field] ?? '';
      const bVal = b.properties?.[sort.field] ?? '';
      if (aVal < bVal) return sort.ascending ? -1 : 1;
      if (aVal > bVal) return sort.ascending ? 1 : -1;
      return 0;
    });

    return features;
  }, [type, listData, search, sort]);

  // Add/remove markers from cluster group based on search match
  useEffect(() => {
    if (!listMarkers || !listRefs || type === 'asset') return;

    const searchActive = !!search?.trim();
    const q = search.trim().toLowerCase();

    Object.values(listRefs).forEach((marker: any) => {
      const name = marker?.feature?.properties?.Name;
      const matches = !searchActive || name?.toLowerCase().includes(q);
      const isOnMap = listMarkers.hasLayer(marker);

      if (matches && !isOnMap) {
        listMarkers.addLayer(marker);
      } else if (!matches && isOnMap) {
        listMarkers.removeLayer(marker);
      }
    });
  }, [search, listMarkers, listRefs, type]);

  const handleFilterChange = (e, type) => {
    if (JSON.stringify(e) !== JSON.stringify(assetFilters)) {
      setAssetFilters(e);
    }
    if (type !== andOr) {
      setAndOr(type);
    }
  };

  const fireClickEvent = (marker) => {
    if (type === 'asset' || type === 'gateway' || type === 'stationary') {
      var visibleLayer = listMarkers.getVisibleParent(marker);

      if (visibleLayer instanceof L.MarkerCluster) {
        listMarkers.zoomToShowLayer(marker, () => {
          marker.fire('click');
        });
      } else {
        marker.fire('click');
      }
    } else {
      marker.fire('click');
    }
  };

  const toggleMarkerShadow = (layer: L.Layer, show: boolean) => {
    if (!layer) return;

    if (layer instanceof L.Marker) {
      const el = layer.getElement?.();
      if (!el) return;
      const container = el.querySelector('.marker-container');
      if (!container) return;
      container.classList.toggle('has-shadow', show);
    } else if (layer instanceof L.MarkerCluster) {
      const el = layer._icon;
      if (!el) return;
      const container = el.querySelector('.marker-container');
      if (!container) return;
      container.classList.toggle('has-shadow', show);
    } else if (layer instanceof L.Path) {
      const originalStyle = layer.options.originalStyle;
      if (!originalStyle) return;

      layer.setStyle({
        ...originalStyle,
        weight: show ? originalStyle.weight + 2 : originalStyle.weight,
      });
    }
  };

  const fireMouseOverEvent = (layer: L.Layer) => {
    if (!layer) return;

    if (layer instanceof L.Path) {
      layer.fire('mouseenter');
      return;
    }

    const visibleLayer =
      typeof (listMarkers as any)?.getVisibleParent === 'function'
        ? (listMarkers as any).getVisibleParent(layer) || layer
        : layer;

    toggleMarkerShadow(visibleLayer, true);
  };

  const fireMouseOutEvent = (layer: L.Layer) => {
    if (!layer) return;

    if (layer instanceof L.Path) {
      layer.fire('mouseout');
      return;
    }

    const visibleLayer =
      typeof (listMarkers as any)?.getVisibleParent === 'function'
        ? (listMarkers as any).getVisibleParent(layer) || layer
        : layer;

    toggleMarkerShadow(visibleLayer, false);
  };

  const handleCsvExport = async () => {
    const formatMapName = (mapName) =>
      mapName
        .split(' ')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join('');

    const formatTypeName = (type) => {
      const formatted = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return formatted === 'Stationary' ? 'StationaryAsset' : formatted;
    };

    const payload = V2FilterMapList(assetFilters, andOr, sort, search);

    const exportPayload = {
      ...payload,
      skip: 0,
    };

    try {
      const response = await Api.post(
        `/v2/maps/${mapID}/${type === 'stationary' ? 'stationary-asset' : type}s/export`,
        exportPayload,
        { responseType: 'blob' },
      );

      if (response?.data instanceof Blob) {
        setCsvData(response.data);
        setCsvName(`${formatMapName(mapName)}_${formatTypeName(type)}s`);
      } else {
        console.error('API did not return a valid Blob:', response);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  return (
    <div className={visible ? 'object-wrapper' : 'object-wrapper-none'}>
      <div className={visible ? 'pinned-controls' : 'pinned-controls-none'}>
        <div className="d-flex flex-row align-items-center justify-content-between">
          <InputGroup>
            <div className="d-flex flex-row">
              <Text className="title-text fw-semibold fs-md me-3">{title ? title : ''}</Text>
              {type === 'asset' && (
                <FilterContainer
                  key={1}
                  onChange={(e, type) => handleFilterChange(e, type)}
                  disabledFilters={disabledFilters}
                  filterReportType={'asset-filter'}
                  ignoreDefaultFilters={true}
                />
              )}
            </div>

            <Form.Control
              id={'Map Search'}
              className="map-search"
              autoComplete="off"
              placeholder="Search by Name"
              onChange={(e) => setSearch(e.target.value)}
            />

            <Icon
              name={'add'}
              title="Close"
              className="sonarIcon-rotate-45 align-sub nav-text-icon"
              variant="light"
              disabled={false}
              size="md"
              showPointer={true}
              onClick={() => {
                setSelected(null);
              }}
            />
          </InputGroup>
          {showExportButton && (
            <Button
              id="betamap-export"
              title="Download"
              className="d-flex flex-row align-items-center justify-content-center"
              variant={'light'}
              onClick={handleCsvExport}
            >
              <i className="sonarIcon sonarIcon-download-file fs-md" />
            </Button>
          )}
        </div>

        {type !== 'overlay' && (
          <div className="d-flex flex-row align-items-center justify-content-between"></div>
        )}
      </div>
      {(isLoading || listData?.features?.length > 0) && (
        <div className="list-container">
          <div key={'headers'}>
            <div className="header-btns">
              <div className="name-col">
                <Button
                  title="Sort by Name"
                  className="btn-light d-flex justify-content-between align-items-center nav-text-icon me-3"
                  variant="light"
                  onClick={() => {
                    if (sort.field !== 'Name') {
                      setSort({ field: 'Name', ascending: true });
                    } else {
                      setSort({
                        field: 'Name',
                        ascending: !sort.ascending,
                      });
                    }
                  }}
                >
                  Name &nbsp;
                  {sort.field === 'Name' && (
                    <i
                      className={
                        sort.ascending
                          ? 'fa fa-arrow-up fs-sm fw-lighter'
                          : 'fa fa-arrow-down fs-sm fw-lighter'
                      }
                    />
                  )}
                </Button>
              </div>

              {type !== 'place' && (
                <div className="checkin-col">
                  <Button
                    title="Sort by Last Check-In"
                    className="btn-light d-flex justify-content-between align-items-center nav-text-icon me-3"
                    variant="light"
                    onClick={() => {
                      if (sort.field !== 'LastCheckIn') {
                        setSort({ field: 'LastCheckIn', ascending: true });
                      } else {
                        setSort({
                          field: 'LastCheckIn',
                          ascending: !sort.ascending,
                        });
                      }
                    }}
                  >
                    Last Check-In &nbsp;
                    {sort.field === 'LastCheckIn' && (
                      <i
                        className={
                          sort.ascending
                            ? 'fa fa-arrow-up fs-sm fw-lighter'
                            : 'fa fa-arrow-down fs-sm fw-lighter'
                        }
                      />
                    )}
                  </Button>
                </div>
              )}

              <div className="btns-col"></div>
            </div>
          </div>
          {isLoading ? (
            <Loading />
          ) : (
            displayedFeatures.map((feature, index) => {
              return (
                <div
                  id={feature.properties.Id}
                  key={index}
                  onMouseOver={() => {
                    try {
                      let marker = listRefs[feature?.properties?.Id];
                      fireMouseOverEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  onMouseOut={() => {
                    try {
                      let marker = listRefs[feature?.properties?.Id];
                      fireMouseOutEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  onClick={() => {
                    try {
                      let marker = listRefs[feature?.properties?.Id];
                      fireClickEvent(marker);
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                >
                  <div className="list-item">
                    <div className="list-info">
                      <div className="name-col">{feature.properties.Name}</div>
                      {feature.properties.LastCheckIn && (
                        <div className="checkin-col">
                          {getFormattedDateTime(feature.properties.LastCheckIn)}
                        </div>
                      )}
                    </div>
                    <div className="btns-col">
                      {(type === 'asset' || type === 'stationary') && (
                        <Button
                          title="Go to Asset"
                          className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                          variant="light"
                          onClick={(e: React.MouseEvent<HTMLElement>) => {
                            history.push('/assets/' + feature.properties.Id);
                            e.stopPropagation();
                          }}
                        >
                          <i className={`sonarIcon sonarIcon-open-in fs-md`} />
                        </Button>
                      )}
                      <Button
                        title="Show more"
                        className="btn-light d-flex justify-content-between align-items-center nav-text-icon"
                        variant="light"
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          setShowMore(feature);
                          e.stopPropagation();
                        }}
                      >
                        <i className={`sonarIcon sonarIcon-h-dots-1 fs-md`} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
